import { useEffect, useState } from "react";
import { Loader2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const n = (x, d = 0) => Number(x || 0).toLocaleString("es-PY", { maximumFractionDigits: d });
const SEL = "rounded-full border border-[#dfe4e1] bg-white px-3 py-1.5 text-sm";

function Filtros({ d, f, setF }) {
  return <div className="flex flex-wrap gap-2 items-center" data-testid="est-filtros">
    <select value={f.campana || d.campana} onChange={(e) => setF({ ...f, campana: e.target.value })} className={SEL} data-testid="est-filtro-campana">
      {d.campanas.map((c) => <option key={c} value={c}>Campaña {c}</option>)}</select>
    <select value={f.distrito || ""} onChange={(e) => setF({ ...f, distrito: e.target.value })} className={SEL} data-testid="est-filtro-distrito">
      <option value="">Todo el país</option>{d.distritos.map((x) => <option key={x.cod} value={x.cod}>{x.nombre}</option>)}</select>
    <select value={f.cultivo || ""} onChange={(e) => setF({ ...f, cultivo: e.target.value })} className={SEL} data-testid="est-filtro-cultivo">
      <option value="">Toda el área mecanizada</option>{Object.entries(d.cultivos).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
  </div>;
}

function Totales({ d }) {
  return <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="est-totales">
    <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-500">Área mecanizada</p>
      <p className="text-2xl font-bold" data-testid="est-total-mecanizada">{n(d.totales.ha_mecanizadas)} ha</p></div>
    {d.totales.cultivos.map((c) => <div key={c.cultivo} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`est-total-${c.cultivo}`}>
      <p className="text-xs text-slate-500">{c.nombre}</p><p className="text-2xl font-bold">{n(c.ha)} ha</p>
      <p className="text-xs text-[#1f7a4d] font-semibold">≈ {n(c.t)} t <span className="text-slate-400 font-normal">({n(d.rindes[c.cultivo], 1)} t/ha)</span></p></div>)}
  </div>;
}

function Top({ d }) {
  const titulo = d.cultivo ? `ha de ${d.cultivos[d.cultivo]}` : "ha mecanizadas";
  return <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="est-top">
    <p className="font-semibold flex items-center gap-2 mb-2"><Trophy className="w-4 h-4 text-amber-500" />Top 10 · {titulo}</p>
    {!d.top.length ? <p className="text-sm text-slate-500" data-testid="est-top-vacio">Todavía no hay parcelas con satélite calculado para este filtro.</p>
      : <table className="w-full text-sm"><tbody>{d.top.map((r, i) => <tr key={r.nombre + i} className="border-t border-slate-100" data-testid={`est-top-fila-${i}`}>
        <td className="py-1.5 pr-2 font-bold text-slate-400 w-6">{i + 1}</td>
        <td className="py-1.5"><b>{r.nombre}</b><span className="text-xs text-slate-500"> · {r.documento || "s/d"} · {r.parcelas} parcela(s) · {r.distritos.join(", ")}</span></td>
        <td className="py-1.5 text-right font-semibold whitespace-nowrap">{n(r.ha, 1)} ha</td></tr>)}</tbody></table>}
  </div>;
}

function PorDistrito({ d }) {
  return <div className="bg-white rounded-xl border border-slate-200 p-4 overflow-x-auto" data-testid="est-por-distrito">
    <p className="font-semibold mb-2">Por distrito</p>
    <table className="w-full text-sm"><thead><tr className="text-xs text-slate-500 text-left"><th>Distrito</th><th className="text-right">Mecanizada</th>
      {Object.values(d.cultivos).map((c) => <th key={c} className="text-right">{c}</th>)}</tr></thead>
      <tbody>{d.por_distrito.map((r) => <tr key={r.cod} className="border-t border-slate-100" data-testid={`est-distrito-${r.cod}`}><td className="py-1">{r.nombre}</td>
        <td className="text-right">{n(r.ha_mecanizadas)}</td>{Object.keys(d.cultivos).map((c) => <td key={c} className="text-right">{n(r[c])}</td>)}</tr>)}</tbody></table>
  </div>;
}

function Rindes({ d, onOk }) {
  const [r, setR] = useState(d.rindes);
  const guardar = () => api.put("/portal/estadisticas/rindes", r).then(() => { toast.success("Rendimientos guardados"); onOk(); }).catch((x) => toast.error(formatApiErrorDetail(x?.response?.data?.detail)));
  return <div className="bg-white rounded-xl border border-slate-200 p-4 text-sm flex flex-wrap items-end gap-3" data-testid="est-rindes">
    <p className="w-full font-semibold">Rendimiento promedio (t/ha) para estimar toneladas</p>
    {Object.entries(d.cultivos).map(([k, v]) => <label key={k} className="text-xs">{v}<input type="number" step="0.1" value={r[k]} onChange={(e) => setR({ ...r, [k]: e.target.value })}
      className="block w-24 rounded-lg border border-slate-200 px-2 py-1 text-sm" data-testid={`est-rinde-${k}`} /></label>)}
    <button onClick={guardar} className="rounded-full bg-[#1f7a4d] text-white px-4 py-1.5 font-semibold" data-testid="est-rindes-guardar">Guardar</button>
  </div>;
}

export default function PortalEstadisticas() {
  const [f, setF] = useState({});
  const [d, setD] = useState(null);
  const [k, setK] = useState(0);
  useEffect(() => {
    api.get("/portal/estadisticas", { params: f }).then((r) => setD(r.data)).catch((x) => toast.error(formatApiErrorDetail(x?.response?.data?.detail)));
  }, [f, k]);
  if (!d) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  return <div className="space-y-4" data-testid="portal-estadisticas">
    <Filtros d={d} f={f} setF={setF} />
    <p className="text-xs text-slate-500" data-testid="est-cobertura">Basado en {n(d.parcelas_con_satelite)} parcelas con satélite calculado de {n(d.parcelas_guardadas)} guardadas · se completa solo cada noche (00:00–06:00).</p>
    <Totales d={d} />
    <Top d={d} />
    {!d.distrito && <PorDistrito d={d} />}
    <Rindes key={JSON.stringify(d.rindes)} d={d} onOk={() => setK(k + 1)} />
    <p className="text-[11px] text-slate-500">{d.nota}</p>
  </div>;
}
